export const SSO_CODE_PARAM = "sso_code";
export const SSO_ERROR_PARAM = "sso_error";
const SSO_CALLBACK_FAILED_MESSAGE =
  "Something went wrong during sign-in. Please try again.";

/**
 * Shared by the `sso_not_available` callback code and the `SSO_NOT_AVAILABLE`
 * mutation code, which report the same condition from different channels.
 */
export const SSO_NOT_AVAILABLE_MESSAGE =
  "Single sign-on isn't available for that account. Contact your admin, or sign in with email and password.";

const parseHashSearchParams = (hash: string) => {
  const value = hash.replace(/^#/, "");
  if (!value.includes("=")) {
    return null;
  }

  return new URLSearchParams(value);
};

/**
 * Reads SSO callback params from the query string and URL hash.
 * Current callback hash params win over legacy query params.
 */
export const readSsoReturnParams = (search: string, hash = "") => {
  const searchParams = new URLSearchParams(search);
  const hashParams = parseHashSearchParams(hash);
  const hashCode = hashParams?.get(SSO_CODE_PARAM) ?? null;
  const hashError = hashParams?.get(SSO_ERROR_PARAM) ?? null;

  if (hashCode || hashError) {
    return { code: hashCode, error: hashError };
  }

  return {
    code: searchParams.get(SSO_CODE_PARAM),
    error: searchParams.get(SSO_ERROR_PARAM),
  };
};

const stripSsoParamsFromHash = (hash: string) => {
  const hashParams = parseHashSearchParams(hash);
  if (!hashParams) {
    return hash;
  }

  if (!hashParams.has(SSO_CODE_PARAM) && !hashParams.has(SSO_ERROR_PARAM)) {
    return hash;
  }

  hashParams.delete(SSO_CODE_PARAM);
  hashParams.delete(SSO_ERROR_PARAM);

  const remaining = hashParams.toString();
  return remaining ? `#${remaining}` : "";
};

/** Drops the SSO return params from the address bar without adding a history entry. */
export const stripSsoReturnParamsFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(SSO_CODE_PARAM);
  url.searchParams.delete(SSO_ERROR_PARAM);
  url.hash = stripSsoParamsFromHash(url.hash);

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);
};

/** Maps `?sso_error=` callback codes to user-facing copy. */
export const resolveSsoErrorMessage = (code: string | null): string => {
  switch (code) {
    case "sso_idp_denied":
      return "Sign-in was canceled or denied by your identity provider. Please try again.";
    case "sso_start_expired":
      return "This sign-in link expired. Please start again.";
    case "sso_state_mismatch":
      return "We could not verify this sign-in attempt. Please try again from the login page.";
    case "sso_organization_mismatch":
      return "Your account belongs to a different organization. Sign in from your organization's login page.";
    case "sso_membership_required":
      return "You do not have access to this workspace yet. Ask your admin for an invite.";
    case "sso_user_inactive":
      return "Your account is deactivated. Contact your admin to restore access.";
    case "sso_not_available":
      return SSO_NOT_AVAILABLE_MESSAGE;
    default:
      return SSO_CALLBACK_FAILED_MESSAGE;
  }
};
